// src/views/ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
// actlnk-3d9f1a05 — action-link router imports: applyActionCode for the non-reset
// modes, plus db + firestore write helpers for the same-browser mirror heal.
import { confirmPasswordReset, verifyPasswordResetCode, applyActionCode } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import logo from '../assets/logo.jpg';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const oobCode = searchParams.get('oobCode');
    // actlnk-a1c4e2b7 — read the Firebase action mode. Legacy links already in inboxes
    // (and any link Firebase emits without a mode) fall back to resetPassword so they
    // keep working exactly as before.
    const mode = searchParams.get('mode') || 'resetPassword';
    const isReset = mode === 'resetPassword';

    const [email, setEmail]               = useState('');
    const [password, setPassword]         = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm]   = useState(false);
    const [status, setStatus]             = useState('verifying'); // verifying | ready | loading | success | error
    const [error, setError]               = useState('');

    // actlnk-b6014d8a — surface the REAL Firebase error code for the action-link
    // branches instead of a blanket "expired" message. (The resetPassword branch keeps
    // its original wording untouched — see below.)
    const describeActionError = (err) => {
        switch (err?.code) {
            case 'auth/expired-action-code':
                return 'This link has expired. Please request a new one.';
            case 'auth/invalid-action-code':
                return 'This link is invalid or has already been used. Please request a new one.';
            case 'auth/user-disabled':
                return 'This account has been disabled. Please contact support.';
            case 'auth/user-not-found':
                return 'We couldn’t find an account for this link. It may have been deleted.';
            default:
                return err?.code
                    ? `We couldn’t complete this request (${err.code}). Please try again or request a new link.`
                    : 'We couldn’t complete this request. Please try again or request a new link.';
        }
    };

    // actlnk-7c2e8b41 — Firebase action-link mode router. Reads `mode` + oobCode on mount
    // and dispatches to the matching handler. The non-reset modes auto-apply on mount
    // (no user input), so they only ever move through verifying → success/error. The
    // resetPassword flow is the DEFAULT and is byte-identical to the original — it also
    // serves invite links (generatePasswordResetLink in addUser/resendInvite/
    // activateFieldWorker) and any legacy/unknown-mode link.
    useEffect(() => {
        if (!oobCode) {
            setStatus('error');
            setError('Invalid or missing reset link. Please request a new one.');
            return;
        }

        // verifyAndChangeEmail → apply the pending email change, then heal the mirror.
        if (mode === 'verifyAndChangeEmail') {
            applyActionCode(auth, oobCode)
                .then(async () => {
                    setStatus('success');
                    // actlnk-5a0d63f2 — same-browser mirror heal. Best-effort ONLY: its own
                    // try/catch so a failed users/{uid} write can never break the success
                    // panel — the email change already succeeded above. The signed-out /
                    // different-browser case is reconciled by ProtectedCrmLayout on next
                    // CRM mount (emailrecon-8192a0b1).
                    try {
                        const user = auth.currentUser;
                        if (user) {
                            await user.reload();
                            const healedEmail = auth.currentUser?.email;
                            if (healedEmail) {
                                await updateDoc(doc(db, 'users', user.uid), { email: healedEmail });
                            }
                        }
                    } catch (healErr) {
                        console.warn('email mirror heal skipped:', healErr);
                    }
                })
                .catch((err) => {
                    setStatus('error');
                    setError(describeActionError(err));
                });
            return;
        }

        // recoverEmail → Firebase-initiated revert link sent to the OLD address.
        if (mode === 'recoverEmail') {
            applyActionCode(auth, oobCode)
                .then(() => setStatus('success'))
                .catch((err) => {
                    setStatus('error');
                    setError(describeActionError(err));
                });
            return;
        }

        // verifyEmail → defensive: this project does not emit it today, but handle it
        // rather than mis-routing it into the password handler.
        if (mode === 'verifyEmail') {
            applyActionCode(auth, oobCode)
                .then(() => setStatus('success'))
                .catch((err) => {
                    setStatus('error');
                    setError(describeActionError(err));
                });
            return;
        }

        // resetPassword (default) + legacy no-mode + any unknown mode → ORIGINAL flow.
        verifyPasswordResetCode(auth, oobCode)
            .then((associatedEmail) => {
                setEmail(associatedEmail);
                setStatus('ready');
            })
            .catch(() => {
                setStatus('error');
                setError('This reset link is invalid or has expired. Please request a new one from the login page.');
            });
    }, [oobCode, mode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match. Please try again.');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setStatus('loading');

        try {
            await confirmPasswordReset(auth, oobCode, password);
            setStatus('success');
            // Redirect to login after 2.5 seconds
            setTimeout(() => {
                navigate('/login', { state: { passwordResetSuccess: true } });
            }, 2500);
        } catch (err) {
            console.error('Password reset error:', err);
            setStatus('error');
            setError('Failed to reset password. Your link may have expired. Please request a new one.');
        }
    };

    return (
        <div className="min-h-screen w-full flex bg-white font-sans text-gray-900">

            {/* --- LEFT SIDE: BRANDING (Desktop Only) --- */}
            <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-indigo-900 via-purple-800 to-orange-500 relative overflow-hidden items-center justify-center p-12">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className="absolute -top-24 -left-24 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-orange-600 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>

                <div className="relative z-10 max-w-lg text-white">
                    <div className="mb-10 flex items-center gap-6">
                        <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-2xl p-3 shrink-0">
                            <img src={logo} alt="Spatchy Logo" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-6xl font-bold tracking-tight drop-shadow-md">Spatchy</span>
                    </div>
                    <h1 className="text-5xl font-extrabold mb-6 leading-tight drop-shadow-sm">
                        Master your business.
                    </h1>
                    <p className="text-xl text-purple-100 font-light leading-relaxed drop-shadow-sm">
                        From the first lead to the final invoice, run your entire business from anywhere in the world.
                    </p>
                </div>
            </div>

            {/* --- RIGHT SIDE: FORM --- */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 md:p-12 relative">

                <div className="w-full max-w-md space-y-8">

                    {/* Mobile Logo */}
                    <div className="lg:hidden flex flex-col items-center mb-8">
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-md p-3 mb-3 border border-gray-100">
                            <img src={logo} alt="Spatchy Logo" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-3xl font-extrabold text-gray-900">Spatchy</h1>
                    </div>

                    {/* actlnk-2f5a9c30 — mode-aware header (resetPassword arm is verbatim original) */}
                    <div className="text-center lg:text-left">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                            {isReset
                                ? 'Reset your password'
                                : mode === 'verifyAndChangeEmail'
                                    ? 'Confirm your email change'
                                    : mode === 'recoverEmail'
                                        ? 'Restore your email address'
                                        : 'Verify your email'}
                        </h2>
                        <p className="mt-2 text-sm text-gray-500">
                            {isReset
                                ? (email ? `Resetting password for ${email}` : 'Create a new password for your account.')
                                : mode === 'verifyAndChangeEmail'
                                    ? 'Finishing the update to your sign-in email.'
                                    : mode === 'recoverEmail'
                                        ? 'Reverting your account to your previous email address.'
                                        : 'Confirming your email address.'}
                        </p>
                    </div>

                    {/* VERIFYING STATE — actlnk-c0f7d283 */}
                    {status === 'verifying' && (
                        <div className="flex flex-col items-center py-8 gap-4">
                            <Loader2 className="animate-spin text-blue-600" size={36} />
                            <p className="text-gray-500 text-sm">
                                {isReset ? 'Verifying your reset link...' : 'Verifying your link...'}
                            </p>
                        </div>
                    )}

                    {/* SUCCESS STATE — actlnk-e81b47c9 (resetPassword arm is verbatim original) */}
                    {status === 'success' && (
                        isReset ? (
                            <div className="p-5 bg-green-50 border border-green-200 rounded-lg flex flex-col items-center gap-3 text-center">
                                <CheckCircle size={36} className="text-green-600" />
                                <div>
                                    <p className="font-semibold text-green-800">Password reset successfully!</p>
                                    <p className="text-sm text-green-600 mt-1">Redirecting you to the login page...</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="p-5 bg-green-50 border border-green-200 rounded-lg flex flex-col items-center gap-3 text-center">
                                    <CheckCircle size={36} className="text-green-600" />
                                    <div>
                                        <p className="font-semibold text-green-800">
                                            {mode === 'recoverEmail'
                                                ? 'Your email address has been restored.'
                                                : mode === 'verifyAndChangeEmail'
                                                    ? 'Your email has been updated.'
                                                    : 'Your email has been verified.'}
                                        </p>
                                        <p className="text-sm text-green-600 mt-1">
                                            {mode === 'recoverEmail'
                                                ? 'For your security, consider resetting your password from the login page.'
                                                : 'You can now sign in with your updated details.'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full py-3 px-4 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-lg transition-all"
                                >
                                    Go to Login
                                </button>
                            </div>
                        )
                    )}

                    {/* ERROR STATE (invalid/expired link) */}
                    {status === 'error' && !oobCode && (
                        <div className="space-y-4">
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-600 text-sm">
                                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-3 px-4 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-lg transition-all"
                            >
                                Back to Login
                            </button>
                        </div>
                    )}

                    {/* READY / LOADING STATE — password form renders ONLY for resetPassword (actlnk-9d3a6f14) */}
                    {isReset && (status === 'ready' || status === 'loading') && (
                        <form onSubmit={handleSubmit} className="space-y-5">

                            {/* New Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all pr-11"
                                        placeholder="Minimum 8 characters"
                                        required
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(p => !p)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                                <div className="relative">
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all pr-11"
                                        placeholder="Re-enter your new password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(p => !p)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        tabIndex={-1}
                                    >
                                        {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
                                    <AlertCircle size={16} />
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={status === 'loading'}
                                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {status === 'loading'
                                    ? <><Loader2 className="animate-spin" size={20} /> Resetting...</>
                                    : 'Reset Password'
                                }
                            </button>

                            <p className="text-center text-sm text-gray-500">
                                Remembered it?{' '}
                                <button
                                    type="button"
                                    onClick={() => navigate('/login')}
                                    className="font-bold text-blue-600 hover:text-blue-500 hover:underline"
                                >
                                    Back to login
                                </button>
                            </p>
                        </form>
                    )}

                    {/* Expired link error with back button */}
                    {status === 'error' && oobCode && (
                        <div className="space-y-4">
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-600 text-sm">
                                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-3 px-4 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-lg transition-all"
                            >
                                Back to Login
                            </button>
                        </div>
                    )}
                </div>

                <div className="absolute bottom-6 text-xs text-gray-400">
                    © {new Date().getFullYear()} Spatchy. All rights reserved.
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;